import React, {useMemo, useState, useEffect, useRef, useCallback} from 'react';
import {t} from 'i18next';
import AurDataGrid from '../../common/aur-data-grid/AurDataGrid';
import {
  DefaultIntakeAuditData,
  getSelectedTabName,
  IFiltersData,
  getInTakeAuditInventoryTableColumns,
  transformDeliveryData,
} from '../../../utils/inTakeAuditReportUtils';
import AurTabMenu from '../../common/aur-tab-menu/AurTabMenu';
import ByCustodianTab, {CustodianType} from './ByCustodianTab';
import CumulativeVolumeReportChart from './component/CumulativeVolumeReportChart';
import AurSelectDropdown from '../../common/aur-select-dropdown/AurSelectDropdown';
import AurDateRangePicker from '../../common/aur-date-range-picker/AurDateRangePicker';
import {v4 as uuidv4} from 'uuid';
import {
  clearFilters,
  downloadAsXLSX,
  getFilterCustodians,
  getFilterItemCategories,
  getIntakeAuditReport,
  getSavedFilters,
  INTAKE_AUDIT_EXPORT_XLSX_URL,
  saveFilters,
} from '../../../api/ReportingManagement';
import AurChipList, {AurChipConfig} from '../../common/aur-chip/AurChipList';
import {useSelector, useDispatch} from 'react-redux';
import {RootState} from '../../../redux/store';
import {
  convertDateStringToObject,
  downloadResponseAsXLSX,
  getFilenameFromHeaders,
  getLocalFormatedDate,
  IArrayData,
  isEmpty,
  removeEmptyArrays,
} from '../../../utils/utils';
import AurPopup from '../../common/aur-popup/AurPopup';
import {Typography} from '@mui/material';
import InTakeAuditHeader from './InTakeAuditHeader';
import {
  resetToDefault,
  setFromDate,
  setHasFilterValueChanged,
  setToDate,
} from '../../../redux/slices/intakeAuditTabSlice';
import {
  CUSTOM_REPORT_COL_HEIGHT,
  DEFAULT_START_DATE_REPORTS,
  failedToLoadStatusCodes,
  INVOICE_REPORT_GRID_ERROR_COMPONENT,
  TABLE_PAGE_SIZE_OPTION,
  TABLE_PER_PAGE_DEFAULT_50_ROW,
} from '../../../constants/constants';
import {setDialogReportName} from '../../../redux/slices/reportsDialogSlice';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import ByDeliveryTab from './ByDeliveryTab';
import AurDataGridErrorOverlay from '../../common/aur-data-grid-error-overlay/AurDataGridErrorOverlay';
import AurDataGridLoadingOverlay from '../../common/aur-data-grid-loading-overlay/AurDataGridLoadingOverlay';
import {
  GA_CLICK_TYPES,
  GA_DATA_MODE_AUTO,
  GA_EVENT_NAMES,
  GA_FILE_EXTENTIONS,
  GA_REGIONS,
  GA_REPORT_TYPE,
  GA_SUBPAGE_TYPE,
} from '../../../constants/analyticsConstants';
import {useAnalytics} from '../../../hooks/useAnalytics';

const TABLE_PER_PAGE_DEFAULT_25_ROW = 25;

interface ItemCategory {
  itemCategory: string;
}

const IntakeAuditTab: React.FC = () => {
  const stringPath = 'reports.inTakeAuditTab.';
  const otherDataItemCategory = 'Other Data';

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(
    TABLE_PER_PAGE_DEFAULT_50_ROW
  );
  const [intakeAuditData, setIntakeAuditData] = useState(
    DefaultIntakeAuditData
  );
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isFailedToLoadInTakeAudit, setIsFailedToLoadInTakeAudit] =
    useState<boolean>(false);
  const dispatch = useDispatch();
  const {trackGAEvent} = useAnalytics();
  const isItemsPerPageChanged = useRef(false);
  const [hasItemsPerPageChanged, setHasItemsPerPageChanged] = useState(false);
  const [chipsConfigChangeTriger, setChipsConfigChangeTriger] = useState(false);
  const [skipTabTracking, setSkipTabTracking] = useState(true);
  const apiResponseTime = useRef('');

  dispatch(
    setDialogReportName(t('reports.projectOverview.tabs.tabIntakeAudit'))
  );

  const handleItemPerPageChange = (pageSize: number) => {
    setItemsPerPage(pageSize);
    if (isItemsPerPageChanged.current) {
      setHasItemsPerPageChanged((prev) => !prev);
    }
  };

  const setInitialTableDataAndPageSize = useCallback(
    (data: any[], totalCount: number) => {
      if (
        totalCount < TABLE_PER_PAGE_DEFAULT_50_ROW &&
        !isItemsPerPageChanged.current
      ) {
        if (selectedTabIndex != 1) {
          //For Delivery Tab itemsPerPage is handled inside the component and not passed into it
          handleItemPerPageChange(TABLE_PER_PAGE_DEFAULT_25_ROW);
        }

        return data.slice(0, TABLE_PER_PAGE_DEFAULT_25_ROW);
      }

      return data;
    },
    [selectedTabIndex]
  );

  const handleOnPagePropsChange = useCallback(
    (itemsPerPageValue: number, currentPageValue: number) => {
      if (itemsPerPageValue != itemsPerPage)
        isItemsPerPageChanged.current = true;

      handleItemPerPageChange(itemsPerPageValue);
      setCurrentPage(currentPageValue);
    },
    [itemsPerPage]
  );

  const [isFetchInTakeAuditLoading, setIsFetchInTakeAuditLoading] =
    useState(false);

  const createChipConfigData = (data?: IFiltersData) => {
    const mapSelections = (items: any, selectedIds: any) =>
      items
        .filter((item: any) => selectedIds?.includes(item.id))
        .map((item: any) => ({id: item.id, value: item.name}));

    return [
      {
        id: 1,
        filterName: t('filters.receivedDate'),
        filterSelection:
          data?.receivedDate?.fromDate && data?.receivedDate?.toDate
            ? [
                {
                  id: 1,
                  value:
                    getLocalFormatedDate(data.receivedDate.fromDate) ===
                    getLocalFormatedDate(data.receivedDate.toDate)
                      ? `${getLocalFormatedDate(data.receivedDate.fromDate)}`
                      : `${getLocalFormatedDate(data.receivedDate.fromDate)} -- ${getLocalFormatedDate(data.receivedDate.toDate)}`,
                },
              ]
            : [],
        showRemove: true,
        onRemove: () => onRemoveFilter(1),
      },
      {
        id: 2,
        filterName: t('filters.itemCategories'),
        filterSelection: data?.itemCategories?.length
          ? mapSelections(itemCategories, data.itemCategories)
          : [],
        showRemove: true,
        onRemove: () => onRemoveFilter(2),
      },
      {
        id: 3,
        filterName: t('filters.custodians'),
        filterSelection: data?.custodians?.length
          ? custodians
              .filter((item: any) =>
                data?.custodians?.includes(item.id.toString())
              )
              .map((item: any) => ({id: item.id, value: item.name}))
          : [],
        showRemove: true,
        onRemove: () => onRemoveFilter(3),
      },
    ];
  };

  const {clientId, projectId, workspaceId} = useSelector(
    (state: RootState) => state.reports
  );

  const reportId = 1;

  const [chipConfigs, setChipConfigs] = useState<AurChipConfig[]>(
    createChipConfigData()
  );

  const [itemCategories, setItemCategories] = useState([]);
  const [custodians, setCustodians] = useState([]);

  const [isGetItemCategoriesLoading, setIsGetItemCategoriesLoading] =
    useState(false);
  const [isGetCustodiansLoading, setIsGetCustodiansLoading] = useState(false);
  const [isFetchSavedFiltersLoading, setIsFetchSavedFiltersLoading] =
    useState(false);
  const [isOnSaveFilterLoading, setIsOnSaveFilterLoading] = useState(false);
  const [isClearAllFilterLoading, setIsClearAllFilterLoading] = useState(false);
  const [isEventTracked, setIsEventTracked] = useState(false);

  const startDate = useSelector(
    (state: RootState) => state.intakeAuditTab.filtersAndChipDate.fromDate
  );

  const endDate = useSelector(
    (state: RootState) => state.intakeAuditTab.filtersAndChipDate.toDate
  );

  const hasFilterValueChanged = useSelector(
    (state: RootState) => state.intakeAuditTab.hasFilterValueChanged
  );

  const renderNoRowsOverlay = useCallback(() => {
    if (isFetchInTakeAuditLoading) {
      return AurDataGridLoadingOverlay;
    }

    if (isFailedToLoadInTakeAudit) return AurDataGridErrorOverlay;

    return undefined;
  }, [isFailedToLoadInTakeAudit, isFetchInTakeAuditLoading]);

  const getItemCategories = async () => {
    setIsGetItemCategoriesLoading(true);
    try {
      if (clientId && projectId && workspaceId) {
        const paramString = `?clientId=${clientId}&projectId=${projectId}&workSpaceId=${workspaceId}`;
        const result = await getFilterItemCategories(paramString);
        if (result?.data?.data) {
          const resultArray = result?.data?.data ?? [];

          let categoryArray = resultArray
            .filter((item: ItemCategory) => !isEmpty(item?.itemCategory))
            .map((item: ItemCategory) => ({
              id: item?.itemCategory,
              name: item?.itemCategory,
            }));

          const index = resultArray.findIndex((item: ItemCategory) =>
            isEmpty(item?.itemCategory)
          );

          if (index >= 0) {
            categoryArray = [
              ...categoryArray.slice(0, index),
              {id: otherDataItemCategory, name: otherDataItemCategory},
              ...categoryArray.slice(index),
            ];
          } else if (isEmpty(resultArray)) {
            categoryArray = [
              {id: otherDataItemCategory, name: otherDataItemCategory},
            ];
          }

          setItemCategories(categoryArray);
        }
        getCustodians();
        setIsGetCustodiansLoading(true);
      }
    } catch (error) {
      console.error('Failed to fetch ItemCategories', error);
    } finally {
      setIsGetItemCategoriesLoading(false);
    }
  };

  const getCustodians = async () => {
    setIsGetCustodiansLoading(true);
    try {
      if (clientId && projectId && workspaceId) {
        const paramString = `?clientId=${clientId}&projectId=${projectId}&workSpaceId=${workspaceId}`;
        const result = await getFilterCustodians(paramString);
        if (result?.data?.data) {
          setCustodians(
            result.data.data.map((item: any) => {
              return {
                id: item.custodianId,
                name: item.custodianName,
              };
            })
          );
        }
      }
    } catch (error) {
      console.error('Failed to fetch custodians:', error);
    } finally {
      setIsGetCustodiansLoading(false);
    }
  };

  const fetchSavedFilters = async () => {
    setIsFetchSavedFiltersLoading(true);
    try {
      if (projectId && workspaceId && clientId && reportId) {
        const paramString = `?clientId=${clientId}&projectId=${projectId}&workSpaceId=${workspaceId}&reportId=${reportId}`;
        const result = await getSavedFilters(paramString);
        if (result?.data?.data) {
          setFiltersAndChipData(result.data.data);
        }
      }
    } catch (error) {
      console.error('Failed to fetch saved filters:', error);
    } finally {
      setIsFetchSavedFiltersLoading(false);
    }
    dispatch(setHasFilterValueChanged(false));
  };
  const fetchIntakeAuditReport = useCallback(async () => {
    setIsFetchInTakeAuditLoading(true);
    try {
      setIsFailedToLoadInTakeAudit(false);
      const paramString = `?pageNumber=${currentPage}&pageSize=${itemsPerPage}&projectId=${projectId}&clientId=${clientId}&workspaceId=${workspaceId}`;
      const requestBody = {
        dataByType: getSelectedTabName(selectedTabIndex),
        receivedDate: {
          fromDate: getLocalFormatedDate(startDate),
          toDate: getLocalFormatedDate(endDate),
        },
        itemCategoryValue: [
          ...(chipConfigs?.[1]?.filterSelection ?? [])
            .filter((item) => item.value !== otherDataItemCategory)
            .map((item) => item.value),
          ...(chipConfigs?.[1]?.filterSelection ?? [])
            .filter((item) => item.value === otherDataItemCategory)
            .map((_) => ''),
        ],
        custodian: [
          ...(chipConfigs?.[2]?.filterSelection ?? []).map(
            (item) => item.value
          ),
        ],
      };
      const result = await getIntakeAuditReport(paramString, requestBody);
      apiResponseTime.current = result.data.responseTime;
      if (getSelectedTabName(selectedTabIndex) === 'DELIVERY') {
        setIntakeAuditData({
          ...result?.data?.data,
          data: {
            totalCumulativeReceivedVolume:
              result?.data?.data?.data.totalCumulativeReceivedVolume,
            deliveryData: setInitialTableDataAndPageSize(
              transformDeliveryData(
                result?.data?.data?.data?.deliveryData ?? []
              ),
              result?.data?.data?.totalCount ?? 0
            ),
          },
        });
      } else if (getSelectedTabName(selectedTabIndex) === 'CUSTODIAN') {
        setIntakeAuditData({
          ...result?.data?.data,
          data: {
            custodianData: setInitialTableDataAndPageSize(
              (result?.data?.data?.data?.custodianData ?? []).map(
                (item: CustodianType) => {
                  return {
                    ...item,
                    id: uuidv4(),
                  };
                }
              ),
              result?.data?.data?.totalCount ?? 0
            ),
            totalCumulativeReceivedVolume:
              result?.data?.data?.data?.totalCumulativeReceivedVolume,
          },
        });
      } else {
        setIntakeAuditData({
          ...result?.data?.data,
          data: {
            inventoryData: setInitialTableDataAndPageSize(
              (result?.data?.data?.data?.inventoryData ?? []).map(
                (item: any) => {
                  return {
                    ...item,
                    id: uuidv4(),
                  };
                }
              ),
              result?.data?.data?.totalCount ?? 0
            ),
            totalCumulativeReceivedVolume:
              result?.data?.data?.data?.totalCumulativeReceivedVolume,
          },
        });
      }
      isItemsPerPageChanged.current = false;
    } catch (error: unknown) {
      apiResponseTime.current = (error as any).responseTime;

      if (failedToLoadStatusCodes.includes((error as {status: number}).status))
        setIsFailedToLoadInTakeAudit(true);
    } finally {
      setIsFetchInTakeAuditLoading(false);
      skipTabTracking &&
        !isEventTracked &&
        trackGAEvent({
          eventName: GA_EVENT_NAMES.PAGE_VIEW,
          page: GA_DATA_MODE_AUTO,
          user: GA_DATA_MODE_AUTO,
          report: {
            name: t('gaTracking.reports.tabIntakeAudit'),
            type: GA_REPORT_TYPE.STANDARD,
            load_time: apiResponseTime.current,
          },
        });
      setIsEventTracked(true);
    }
  }, [
    chipConfigs,
    clientId,
    currentPage,
    endDate,
    isEventTracked,
    itemsPerPage,
    projectId,
    selectedTabIndex,
    setInitialTableDataAndPageSize,
    skipTabTracking,
    startDate,
    trackGAEvent,
    workspaceId,
  ]);

  useEffect(() => {
    if (custodians.length > 0 && itemCategories.length > 0) {
      fetchSavedFilters();
    }
  }, [custodians, itemCategories]);

  useEffect(() => {
    getItemCategories();
  }, [workspaceId]);

  useEffect(() => {
    fetchIntakeAuditReport();
  }, [
    selectedTabIndex,
    currentPage,
    projectId,
    workspaceId,
    hasItemsPerPageChanged,
    //itemsPerPage,
    chipsConfigChangeTriger,
  ]);

  const setFiltersAndChipData = (data: IFiltersData) => {
    setChipConfigs(createChipConfigData(data));
    if (data.receivedDate?.fromDate && data.receivedDate?.toDate) {
      dispatch(setFromDate(getLocalFormatedDate(data.receivedDate?.fromDate)));
      dispatch(setToDate(getLocalFormatedDate(data.receivedDate?.toDate)));
    }
  };

  const [clearAllModal, setClearAllModal] = useState<boolean>(false);
  const [resetToDefaultModal, setResetToDefaultModal] =
    useState<boolean>(false);
  const [exportAsCSVModal, setExportAsCSVModal] = useState<boolean>(false);
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState(
    (tableContainerRef.current?.offsetHeight ?? 500) +
      INVOICE_REPORT_GRID_ERROR_COMPONENT
  );
  useEffect(() => {
    const updateHeight = () => {
      if (tableContainerRef.current) {
        setSize(
          tableContainerRef.current.offsetHeight - CUSTOM_REPORT_COL_HEIGHT
        );
      }
    };
    window.addEventListener('resize', updateHeight);
    updateHeight();

    return () => window.removeEventListener('resize', updateHeight);
  });

  const getUpdatedChipFilterSelection = (
    filter: AurChipConfig,
    start: string,
    end: string
  ) => {
    return filter.filterSelection.map((selection) =>
      selection.id === 1
        ? {
            ...selection,
            value:
              getLocalFormatedDate(start) === getLocalFormatedDate(end)
                ? `${getLocalFormatedDate(start)}`
                : `${getLocalFormatedDate(start)} -- ${getLocalFormatedDate(end)}`,
          }
        : selection
    );
  };

  const handleOnDateChange = (start: string, end: string) => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.FILTER,
      click: {
        name: t('dateRangePicker.datePosted'),
        type: GA_CLICK_TYPES.DROPDOWN,
        region: GA_REGIONS.BODY,
        action: t('gaTracking.reports.filterReport'),
      },
    });
    setIsLoading(true);
    dispatch(setHasFilterValueChanged(true));
    setChipConfigs((prevConfig) =>
      prevConfig.map((filter) =>
        filter.id === 1
          ? {
              ...filter,
              filterSelection:
                filter.filterSelection.length > 0
                  ? getUpdatedChipFilterSelection(filter, start, end)
                  : [
                      {
                        id: 1,
                        value:
                          getLocalFormatedDate(start) ===
                          getLocalFormatedDate(end)
                            ? `${getLocalFormatedDate(start)}`
                            : `${getLocalFormatedDate(start)} -- ${getLocalFormatedDate(end)}`,
                      },
                    ],
            }
          : filter
      )
    );

    dispatch(setFromDate(getLocalFormatedDate(start)));
    dispatch(setToDate(getLocalFormatedDate(end)));
    setChipsConfigChangeTriger((e) => !e);
    setTimeout(() => {
      setIsLoading(false);
    }, 2000);
  };

  const onRemoveFilter = (filterId: number) => {
    dispatch(setHasFilterValueChanged(true));
    setChipConfigs((prevConfig) =>
      prevConfig.map((filter) =>
        filter.id === filterId ? {...filter, filterSelection: []} : filter
      )
    );
    setChipsConfigChangeTriger((e) => !e);
  };

  const handleOnClearAll = () => {
    setClearAllModal(true);
  };
  const handleOnSave = () => {
    onSaveFilters();
  };

  const onSaveFilters = async () => {
    setIsOnSaveFilterLoading(true);
    try {
      const paramString = `?clientId=${clientId}&projectId=${projectId}&workspaceId=${workspaceId}`;
      const requestBody = getSelectedFilters();
      await saveFilters(reportId, paramString, requestBody);
      const savedFilters = requestBody as any;
      setFiltersAndChipData(savedFilters);
      dispatch(setHasFilterValueChanged(false));
    } catch (error) {
      console.error('Failed to save filters:', error);
    } finally {
      setIsOnSaveFilterLoading(false);
    }
  };

  const onClearFilters = async () => {
    setIsClearAllFilterLoading(true);
    try {
      if (projectId && workspaceId && clientId && reportId) {
        const paramString = `?clientId=${clientId}&projectId=${projectId}&workspaceId=${workspaceId}`;
        await clearFilters(reportId, paramString);
        handleClearAllModalOptionSelected();
      }
    } catch (error) {
      console.error('Failed to Clear saved filters:', error);
    } finally {
      setIsClearAllFilterLoading(false);
    }
  };

  const handleResetToDefaultModalOptionSelected = () => {
    closeModalHandler();
    onClearFilters();
  };

  const handleResetToDefault = () => {
    setResetToDefaultModal(true);
  };

  const closeModalHandler = () => {
    setClearAllModal(false);
    setResetToDefaultModal(false);
  };

  const handleClearAllModalOptionSelected = () => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.FILTER,
      click: {
        name: t('gaTracking.global.clearAll'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.BODY,
        action: t('gaTracking.reports.filterReport'),
      },
    });
    closeModalHandler();
    setChipConfigs((prevConfig) =>
      prevConfig.map((filter) => ({...filter, filterSelection: []}))
    );
    dispatch(resetToDefault());
    setChipsConfigChangeTriger((e) => !e);
  };

  const getSelectedFilters = () => {
    const result: IArrayData = {
      custodians: [],
      itemCategories: [],
      receivedDate: {
        fromDate: getLocalFormatedDate(new Date()),
        toDate: getLocalFormatedDate(new Date()),
      },
    };
    chipConfigs.forEach((chipConfig) => {
      switch (chipConfig.filterName) {
        case 'Custodians':
          result.custodians = chipConfig.filterSelection.map((item) =>
            item.id.toString()
          );
          break;
        case 'Item Categories':
          result.itemCategories = chipConfig.filterSelection.map((item) =>
            item.id.toString()
          );
          break;
        case 'Received Date':
          if (startDate && endDate) {
            result.receivedDate = {
              fromDate: getLocalFormatedDate(startDate),
              toDate: getLocalFormatedDate(endDate),
            };
          }
          break;
        default:
          break;
      }
    });
    return removeEmptyArrays(result);
  };
  const handleOnDropdownDataChanged = (id: any, filterData: any) => {
    if (id && filterData && chipConfigs?.[id]) {
      setChipConfigs((prevChipsConfig) =>
        prevChipsConfig.map((chipConfig, index) =>
          index === id
            ? {...chipConfig, filterSelection: filterData}
            : chipConfig
        )
      );
      dispatch(setHasFilterValueChanged(true));
      setChipsConfigChangeTriger((e) => !e);
    }
  };

  const handleOnTabChange = (tabIndex: number) => {
    tabIndex !== selectedTabIndex &&
      setItemsPerPage(TABLE_PER_PAGE_DEFAULT_50_ROW);
    setIntakeAuditData(DefaultIntakeAuditData);
    setCurrentPage(1);
    isItemsPerPageChanged.current = false;
    setSelectedTabIndex(tabIndex);
    setSkipTabTracking(false);
    trackGAEvent({
      eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
      subPage: {
        subpage_name: tabs[tabIndex].header.text,
        subpage_type: GA_SUBPAGE_TYPE.TAB,
      },
      user: GA_DATA_MODE_AUTO,
    });
  };
  const exportXlsxOnClick = () => {
    exportAsXlsx();
    trackGAEvent({
      eventName: GA_EVENT_NAMES.EXPORT,
      click: {
        name: t('gaTracking.reports.exportOption.xlsx'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.BODY,
        action: t('gaTracking.reports.exportReport'),
      },
      file_extension: GA_FILE_EXTENTIONS.XLSX,
      subPage: {
        subpage_name: tabs[selectedTabIndex].header.text,
        subpage_type: GA_SUBPAGE_TYPE.TAB,
      },
    });
  };
  const exportAsXlsx = async () => {
    setExportAsCSVModal(true);
    try {
      const paramString = `?projectId=${projectId}&clientId=${clientId}&workspaceId=${workspaceId}`;
      const requestBody = {
        dataByType: getSelectedTabName(selectedTabIndex),
        receivedDate: {
          fromDate: getLocalFormatedDate(startDate),
          toDate: getLocalFormatedDate(endDate),
        },
        itemCategoryValue: [
          ...(chipConfigs?.[1]?.filterSelection ?? []).map(
            (item) => item.value
          ),
        ],
        custodian: [
          ...(chipConfigs?.[2]?.filterSelection ?? []).map(
            (item) => item.value
          ),
        ],
      };
      const result = await downloadAsXLSX(
        INTAKE_AUDIT_EXPORT_XLSX_URL,
        paramString,
        requestBody
      );

      const fileName =
        getFilenameFromHeaders(result.headers['content-disposition']) ??
        `${workspaceId}_${Date.now()}`;
      downloadResponseAsXLSX(result, fileName, false);
    } catch (error) {
      console.error('Failed to Export Inatke Audit Report:', error);
    }
  };
  const tabs = useMemo(
    () => [
      {
        header: {
          text: t('reports.intakeAudit.tab.byCustodian'),
        },
        content: (
          <ByCustodianTab
            custodianData={intakeAuditData?.data?.custodianData}
            totalCumulativeReceivedVolume={
              intakeAuditData?.data?.totalCumulativeReceivedVolume
            }
            totalRecordsCount={intakeAuditData?.totalCount}
            onPagePropsChange={(itemsPerPage, currentPage) =>
              handleOnPagePropsChange(itemsPerPage, currentPage)
            }
            onRefresh={fetchIntakeAuditReport}
            isChartFailed={isFailedToLoadInTakeAudit}
            isChartLoading={isFetchInTakeAuditLoading}
            size={size}
            itemPerPage={itemsPerPage}
          />
        ),
        index: 0,
      },
      {
        header: {
          text: t('reports.intakeAudit.tab.byDelivery'),
        },
        content: (
          <ByDeliveryTab
            custodianData={intakeAuditData.data?.deliveryData}
            totalCumulativeReceivedVolume={
              intakeAuditData?.data?.totalCumulativeReceivedVolume
            }
            totalRecordsCount={intakeAuditData?.totalCount}
            onPagePropsChange={(itemsPerPage, currentPage) =>
              handleOnPagePropsChange(itemsPerPage, currentPage)
            }
            onRefresh={fetchIntakeAuditReport}
            isChartFailed={isFailedToLoadInTakeAudit}
            isChartLoading={isFetchInTakeAuditLoading}
            size={size}
          />
        ),
        index: 1,
      },
      {
        header: {
          text: t('reports.intakeAudit.tab.byInventory'),
        },
        content: (
          <>
            <CumulativeVolumeReportChart
              custodianData={[
                {
                  emailCompressed:
                    intakeAuditData.data?.totalCumulativeReceivedVolume.email,
                  otherData:
                    intakeAuditData.data?.totalCumulativeReceivedVolume
                      .otherData,
                  externallyProcessed:
                    intakeAuditData.data?.totalCumulativeReceivedVolume
                      .externallyProcessed,
                  receivedDate: '',
                  custodianName: '',
                  id: '',
                  subTotal: '',
                },
              ]}
              totalCumulativeReceivedVolume={
                intakeAuditData.data?.totalCumulativeReceivedVolume
              }
              isChartFailed={isFailedToLoadInTakeAudit}
              isChartLoading={isFetchInTakeAuditLoading}
              onRefresh={fetchIntakeAuditReport}
            />
            <AurDataGrid
              columns={getInTakeAuditInventoryTableColumns()}
              rows={intakeAuditData.data?.inventoryData}
              pageSizeOptions={TABLE_PAGE_SIZE_OPTION}
              checkBoxVisible={false}
              entityType={''}
              visibleToolTip
              columnHeaderHeight={64}
              className="inTakeAuditInventoryTable"
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={(perPage) => {
                handleOnPagePropsChange(perPage, currentPage);
              }}
              getRowId={(item) => item.id}
              totalRowCount={intakeAuditData.totalCount}
              containerHeight={size}
              noRowsOverlay={renderNoRowsOverlay()}
              onRefresh={fetchIntakeAuditReport}
              trackPaginationActionName={t(
                'gaTracking.global.reportPagination'
              )}
            />
          </>
        ),
        index: 2,
      },
    ],
    [
      intakeAuditData.data?.custodianData,
      intakeAuditData.data?.totalCumulativeReceivedVolume,
      intakeAuditData.data?.deliveryData,
      intakeAuditData.data?.inventoryData,
      intakeAuditData.totalCount,
      fetchIntakeAuditReport,
      isFailedToLoadInTakeAudit,
      isFetchInTakeAuditLoading,
      size,
      itemsPerPage,
      currentPage,
      renderNoRowsOverlay,
      handleOnPagePropsChange,
    ]
  );

  return (
    <>
      <InTakeAuditHeader />
      <div className="intake-audit-report-detail">
        <div className="processing-summary-description">
          <Typography className="typo-text subhead1_size mist-100 ">
            {t('reports.inTakeAuditTab.descriptionHeading')}
          </Typography>
          <Typography className="typo-text body2_size color-mist-400 detail-sub-description">
            {t('reports.inTakeAuditTab.descriptionSubHeading')}
          </Typography>
        </div>
        <div
          style={{
            display: 'flex',
            padding: '0px 50px 48px 0px',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: '8px',
            }}
          >
            <AurDateRangePicker
              startYearFromRange={
                new Date().getFullYear() -
                new Date(DEFAULT_START_DATE_REPORTS).getFullYear() +
                1 // Added 1 here because need to include default start date year also in dropdown. for example current is 2025 - 1975 then only 50 item show in dropdwon till 1976
              }
              onDateChange={handleOnDateChange}
              initialEndDate={convertDateStringToObject(endDate)}
              initialStartDate={convertDateStringToObject(startDate)}
              shouldShowApplyButton={true}
              isLoading={isLoading}
            />
            <div
              style={{
                width: '300px',
              }}
            >
              <AurSelectDropdown
                items={itemCategories}
                searchPlaceHolder={t('filter.itemCategory')}
                itemsCountwithNoScroll={3}
                itemDisplay="name"
                itemKey="id"
                onClearClicked={() => onRemoveFilter(2)}
                selectAllLabel={t('filter.selectAll')}
                dropdownLabel={t('filter.itemCategory')}
                onSelectionChange={(data) => {
                  trackGAEvent({
                    eventName: GA_EVENT_NAMES.FILTER,
                    click: {
                      name: t('gaTracking.reports.filter.itemCategory'),
                      type: GA_CLICK_TYPES.DROPDOWN,
                      region: GA_REGIONS.BODY,
                      action: t('gaTracking.reports.filterReport'),
                    },
                  });
                  handleOnDropdownDataChanged(
                    1,
                    data?.map((item) => {
                      return {
                        id: item.id,
                        value: item.name,
                      };
                    })
                  );
                }}
                search={itemCategories?.length > 11}
                hasValidationError={false}
                validationText={t('validation.selectAtLeastOneProject')}
                helperText=""
                aria-label={t('aria.projectSelectItemCategory')}
                sx={{
                  width: '300px',
                }}
                defaultSelection={chipConfigs?.[1]?.filterSelection?.map(
                  (item) => {
                    return {
                      id: item.id,
                      name: item.value,
                    };
                  }
                )}
                className="intake-audit-dropdown"
              />
            </div>
            <div
              style={{
                width: '300px',
              }}
            >
              <AurSelectDropdown
                sx={{
                  width: '300px',
                }}
                items={custodians}
                searchPlaceHolder={t('filter.custodian')}
                itemsCountwithNoScroll={3}
                onClearClicked={() => onRemoveFilter(3)}
                itemDisplay="name"
                itemKey="id"
                selectAllLabel={t('filter.selectAll')}
                dropdownLabel={t('filter.custodian')}
                search={custodians?.length > 11}
                onSelectionChange={(data) => {
                  trackGAEvent({
                    eventName: GA_EVENT_NAMES.FILTER,
                    click: {
                      name: t('gaTracking.reports.filter.custodian'),
                      type: GA_CLICK_TYPES.DROPDOWN,
                      region: GA_REGIONS.BODY,
                      action: t('gaTracking.reports.filterReport'),
                    },
                  });
                  handleOnDropdownDataChanged(
                    2,
                    data?.map((item) => {
                      return {
                        id: item.id,
                        value: item.name,
                      };
                    })
                  );
                }}
                hasValidationError={false}
                validationText={t('validation.selectAtLeastOneProject')}
                helperText=""
                aria-label={t('aria.projectSelectCustodian')}
                defaultSelection={chipConfigs?.[2]?.filterSelection?.map(
                  (item) => {
                    return {
                      id: item.id,
                      name: item.value,
                    };
                  }
                )}
                className="intake-audit-dropdown"
              />
            </div>
          </div>
          {(chipConfigs[0].filterSelection.length > 0 ||
            chipConfigs[1].filterSelection.length > 0 ||
            chipConfigs[2].filterSelection.length > 0) && (
            <div
              style={{
                paddingTop: '22px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}
            >
              <AurChipList
                onResetToDefault={handleResetToDefault}
                onSave={handleOnSave}
                onClearAll={handleOnClearAll}
                hasFilterValueChanged={hasFilterValueChanged}
                chipConfigs={chipConfigs}
                isOnSaveLoading={isOnSaveFilterLoading}
                isClearAllLoading={isClearAllFilterLoading}
                trackFilter={true}
              />
            </div>
          )}
        </div>
        <div className="tab-menu">
          <AurTabMenu
            headerClasses="typo-text subhead4_size intake-audit-report-tab-menu-header"
            tabs={tabs}
            onTabChange={(index: number) => handleOnTabChange(index)}
            width={'100%'}
            tabMenuHeader={t('reports.intakeAudit.tab.title')}
            tabHeaderClassName="intake-audit-report-tabs-heading-text"
            tabHeaderMainContainerClassName="intake-audit-report-tab-menu-main-container"
            tabHeaderSubContainerClassName="intake-audit-report-tab-sub-main-container"
            showTabHeaderButton={true}
            onClickTabHeaderButton={exportXlsxOnClick}
          />
        </div>
        {clearAllModal && (
          <AurPopup
            className="aur-unsaved-changes-popup"
            open={clearAllModal}
            onOk={handleClearAllModalOptionSelected}
            onCancel={closeModalHandler}
            popupTitle={t(stringPath + 'filterPopup.title')}
            popupHeading={t(stringPath + 'filterPopup.clearHeading')}
            popupContent={t(stringPath + 'filterPopup.content')}
            showPopupHeading={true}
            showPopupTitle={true}
            showCloseIcon={true}
            labelActionCancel={t(stringPath + 'filterPopup.cancelButton')}
            labelActionOk={t(stringPath + 'filterPopup.clearButton')}
            exitButtonHint={t('common.clearAllPopup.cacnelToClearHint')}
            continueButtonHint={t('common.clearAllPopup.confirmToClearHint')}
            cancelButtonTitle=""
          />
        )}
        {resetToDefaultModal && (
          <AurPopup
            className="aur-unsaved-changes-popup"
            open={resetToDefaultModal}
            onOk={handleResetToDefaultModalOptionSelected}
            onCancel={closeModalHandler}
            popupTitle={t(stringPath + 'filterPopup.title')}
            popupHeading={t(stringPath + 'filterPopup.resetHeading')}
            popupContent={t(stringPath + 'filterPopup.content')}
            showPopupHeading={true}
            showPopupTitle={true}
            showCloseIcon={true}
            labelActionCancel={t(stringPath + 'filterPopup.cancelButton')}
            labelActionOk={t(stringPath + 'filterPopup.resetButton')}
            exitButtonHint={t('common.clearAllPopup.cancelResetToDefaultHint')}
            continueButtonHint={t(
              'common.clearAllPopup.confirmResetToDefaultHint'
            )}
            cancelButtonTitle=""
          />
        )}
        {exportAsCSVModal && (
          <AurPopup
            className="aur-unsaved-changes-popup"
            open={exportAsCSVModal}
            onCancel={() => setExportAsCSVModal(false)}
            onOk={() => setExportAsCSVModal(false)}
            popupHeading={t('common.csvDownloadPopup.heading')}
            popupContent={t('common.csvDownloadPopup.content')}
            showPopupHeading={true}
            showPopupTitle={true}
            showCloseIcon={true}
            labelActionOk={t('common.csvDownloadPopup.labelActionOk')}
            showCancelOption={false}
            buttonStyle="stacked"
            cancelButtonTitle=""
            continueButtonHint={t(
              'common.clearAllPopup.confirmToCloseModalHint'
            )}
          />
        )}
      </div>
      {
        <AurLoadingIndicator
          overlay={true}
          isOpen={
            isFetchSavedFiltersLoading ||
            isGetCustodiansLoading ||
            isGetItemCategoriesLoading
          }
        />
      }
    </>
  );
};

export default IntakeAuditTab;
